import type { ReactNode } from 'react';
import { normaliseEventPayload, type EventFacts, type EventOccurrence, type EventPayload, type RelatedEvent } from '../../eventsApi';
import { formatDate, formatDateTime, formatLocalDay, formatLocalDayShort, relativeAge } from '../../format';
import { Chip, Notice } from '../ui';
import { endDayNote, formatRescheduledFrom, placeLine, startTime } from './EventCard';
import './events.css';

/**
 * The details body for a card version with payload_type 'event.v1'.
 *
 * The modal renders the title; this renders the facts. Event status and ticket
 * availability are separate lines, doors and start are separate lines, and a
 * fact the source did not state is either left out (price, age) or named as
 * unknown (time) — never guessed.
 *
 * `occurrence` is the live agenda record when one is at hand; it adds when the
 * source was last checked, listing presence and related records. Without it the
 * payload's own `observed_at` is shown as "Stand".
 */
export function EventDetails({ payload, occurrence }: { payload: EventPayload; occurrence?: EventOccurrence }) {
  const p = normaliseEventPayload(payload);
  // Live facts win over the card version's snapshot when both are at hand.
  const f: EventFacts = occurrence ?? p;
  const time = startTime(f);
  const endNote = endDayNote(f);
  const place = placeLine(f);
  const related = occurrence?.related ?? [];
  const altLinks = Object.entries(p.alternate_urls).filter(([, url]) => !!url && url !== f.canonical_url);
  const evidence = [...new Set(p.evidence_urls)].filter((u) => u !== f.canonical_url && u !== f.ticket_url);

  return (
    <div className="event-detail">
      {f.subtitle && (
        <p className="lead" lang={f.language || undefined}>
          {f.subtitle}
        </p>
      )}

      <StatusNotice f={f} />
      {occurrence?.missing_since && (
        <Notice kind="warn" title={`Nicht mehr gelistet seit ${formatDate(occurrence.missing_since)}`}>
          Der Termin fehlt seither in der Liste von {f.source_name}. Das heisst nicht, dass er abgesagt ist – bitte auf der Originalseite prüfen.
        </Notice>
      )}

      <dl className="event-facts">
        <Fact label="Datum">{fullDay(f.local_date)}</Fact>
        {f.doors_local && <Fact label="Einlass">{f.doors_local}</Fact>}
        <Fact label="Beginn">{time ?? <span className="muted">Zeit offen – die Quelle nennt keine Uhrzeit</span>}</Fact>
        {f.end_local && (
          <Fact label="Ende">
            {f.end_local}
            {endNote && f.end_local_date && (
              <>
                {' '}
                <span className="event-plus">{endNote}</span> <span className="muted">({formatLocalDayShort(f.end_local_date)})</span>
              </>
            )}
          </Fact>
        )}
        <Fact label="Ort">
          {place || <span className="muted">Nicht angegeben</span>}
          {f.venue_basis && isPublisherDefault(f.venue_basis) && (
            <span className="event-basis">Standardort des Veranstalters – beim Termin selbst nicht genannt.</span>
          )}
        </Fact>
        <Fact label="Region">
          {LOCALITY_LABELS[f.locality] ?? f.locality}
          {f.locality_basis && <span className="event-basis">Grundlage: {f.locality_basis}</span>}
        </Fact>
        <Fact label="Status">
          <EventStatusText f={f} />
        </Fact>
        <Fact label="Tickets">
          <TicketText f={f} />
        </Fact>
        {priceText(f) && <Fact label="Preis">{priceText(f)}</Fact>}
        {f.min_age !== null && f.min_age !== undefined && <Fact label="Alter">ab {f.min_age} Jahren</Fact>}
        {(f.categories.length > 0 || f.category) && (
          <Fact label="Kategorie">{(f.categories.length > 0 ? f.categories : [f.category]).filter(Boolean).join(', ')}</Fact>
        )}
        {f.artists.length > 0 && <Fact label="Mitwirkende">{f.artists.join(', ')}</Fact>}
        {f.organizer && <Fact label="Veranstalter">{f.organizer}</Fact>}
        <Fact label="Quelle">{f.source_name}</Fact>
        {occurrence ? (
          <Fact label="Zuletzt geprüft">
            <time dateTime={occurrence.last_checked_at} title={formatDateTime(occurrence.last_checked_at)}>
              {relativeAge(occurrence.last_checked_at)}
            </time>
          </Fact>
        ) : (
          <Fact label="Stand">
            <time dateTime={p.observed_at} title={formatDateTime(p.observed_at)}>
              {formatDateTime(p.observed_at)}
            </time>
          </Fact>
        )}
      </dl>

      <div className="button-row event-links">
        <a className="button primary" href={f.canonical_url} target="_blank" rel="noopener noreferrer">
          Originalseite bei {f.source_name} ↗
        </a>
        {f.ticket_url && f.event_status !== 'cancelled' && (
          <a className="button" href={f.ticket_url} target="_blank" rel="noopener noreferrer">
            Tickets ↗
          </a>
        )}
        {altLinks.map(([lang, url]) => (
          <a key={lang} className="small-button" href={url} target="_blank" rel="noopener noreferrer" hrefLang={lang} lang={lang}>
            {languageName(lang)} ↗
          </a>
        ))}
      </div>

      {f.description && (
        <>
          <h3>Beschreibung</h3>
          <p className="lead event-description" lang={f.language || undefined}>
            {f.description}
          </p>
          <p className="muted small">Text von {f.source_name}.</p>
        </>
      )}

      {related.length > 0 && <Related items={related} />}

      {evidence.length > 0 && (
        <>
          <h3>Belege</h3>
          <ul className="plain event-evidence">
            {evidence.map((u) => (
              <li key={u}>
                <a href={u} target="_blank" rel="noopener noreferrer">
                  {u}
                </a>
              </li>
            ))}
          </ul>
        </>
      )}

      <p className="muted small event-smallprint">
        Zeiten in {f.timezone}
        {f.timezone_basis ? ` (Grundlage: ${f.timezone_basis})` : ''}. {f.date_precision === 'date' ? 'Die Quelle nennt nur das Datum. ' : ''}
        Die Angaben stammen direkt von der Quelle, ohne Modell.
      </p>
    </div>
  );
}

const LOCALITY_LABELS: Record<string, string> = {
  'basel-region': 'Basel & Region',
  other: 'Anderer Ort',
  unknown: 'Ort unklar',
};

const FIELD_LABELS: Record<string, string> = {
  venue: 'Ort',
  city: 'Stadt',
  local_date: 'Datum',
  start_local: 'Beginn',
  doors_local: 'Einlass',
  end_local: 'Ende',
  title: 'Titel',
  event_status: 'Status',
  ticket_status: 'Tickets',
  price_min: 'Preis ab',
  price_max: 'Preis bis',
  min_age: 'Alter',
};

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

function fullDay(day: string): string {
  const year = day.slice(0, 4);
  return /^\d{4}$/.test(year) ? `${formatLocalDay(day)} ${year}` : formatLocalDay(day);
}

/** A venue that was filled in from the publisher's own address, not stated for the event. */
export function isPublisherDefault(basis: string): boolean {
  return /default|publisher|organi[sz]er|veranstalter|standard/i.test(basis);
}

function StatusNotice({ f }: { f: EventFacts }) {
  if (f.event_status === 'cancelled') {
    return <Notice kind="danger" title="Abgesagt">{f.source_name} meldet diesen Termin als abgesagt.</Notice>;
  }
  if (f.event_status === 'postponed') {
    return <Notice kind="warn" title="Verschoben">Ein neues Datum ist noch nicht bekannt.</Notice>;
  }
  if (f.event_status === 'rescheduled') {
    return (
      <Notice kind="warn" title="Neues Datum">
        {f.rescheduled_from ? `Ursprünglich ${formatRescheduledFrom(f.rescheduled_from)}; jetzt ${fullDay(f.local_date)}.` : `Jetzt ${fullDay(f.local_date)}.`}
      </Notice>
    );
  }
  return null;
}

function EventStatusText({ f }: { f: EventFacts }) {
  switch (f.event_status) {
    case 'scheduled':
      return <>Findet statt</>;
    case 'cancelled':
      return <Chip kind="danger">Abgesagt</Chip>;
    case 'postponed':
      return <Chip kind="warn">Verschoben</Chip>;
    case 'rescheduled':
      return (
        <>
          <Chip kind="warn">Neues Datum</Chip>
          {f.rescheduled_from && <span className="muted"> vorher {formatRescheduledFrom(f.rescheduled_from)}</span>}
        </>
      );
    default:
      return <span className="muted">Von der Quelle nicht angegeben</span>;
  }
}

function TicketText({ f }: { f: EventFacts }) {
  if (f.event_status === 'cancelled') return <span className="muted">Entfällt (abgesagt)</span>;
  if (f.ticket_status === 'sold_out') return <Chip kind="info">Ausverkauft</Chip>;
  if (f.ticket_status === 'available') return <>Erhältlich</>;
  return (
    <span className="muted">
      Verfügbarkeit unbekannt{f.ticket_url ? ' – ein Ticketlink allein heisst nicht, dass es noch Tickets gibt' : ''}
    </span>
  );
}

function money(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

/** Only what the source stated: a range, a single price or free entry; null when unknown. */
export function priceText(f: Pick<EventFacts, 'price_min' | 'price_max' | 'currency'>): string | null {
  const min = f.price_min ?? null;
  const max = f.price_max ?? null;
  if (min === null && max === null) return null;
  const cur = f.currency ? `${f.currency} ` : '';
  if (min !== null && max !== null) {
    if (min === 0 && max === 0) return 'Eintritt frei';
    return min === max ? `${cur}${money(min)}` : `${cur}${money(min)}–${money(max)}`;
  }
  if (min !== null) return `ab ${cur}${money(min)}`;
  return `bis ${cur}${money(max!)}`;
}

export function languageName(code: string): string {
  try {
    const name = new Intl.DisplayNames(['de-CH'], { type: 'language' }).of(code);
    if (name && name.toLowerCase() !== code.toLowerCase()) return `Auf ${name}`;
  } catch {
    /* an unknown code falls through */
  }
  return `Sprache ${code.toUpperCase()}`;
}

function Related({ items }: { items: RelatedEvent[] }) {
  return (
    <>
      <h3>Auch bei anderen Quellen</h3>
      <p className="muted small">Derselbe Termin, von einer anderen Quelle gelistet. Beide Einträge bleiben erhalten.</p>
      {items.map((r) => (
        <div key={r.id} className="source-entry">
          <p className="source-title">
            <a href={r.canonical_url} target="_blank" rel="noopener noreferrer">
              {r.title}
            </a>
          </p>
          <p className="muted small">
            {r.source_name} · {formatLocalDayShort(r.local_date)}
            {r.start_local ? ` · ${r.start_local}` : ' · Zeit offen'}
            {r.venue ? ` · ${r.venue}` : ''}
          </p>
          {r.conflicts.length > 0 && (
            <div className="event-conflicts">
              <span className="tag tag-warn">Angaben weichen ab</span>
              <ul className="plain">
                {r.conflicts.map((c) => (
                  <li key={c.field}>
                    <strong>{FIELD_LABELS[c.field] ?? c.field}:</strong> hier «{c.this_value ?? 'nicht angegeben'}», bei {r.source_name} «
                    {c.other_value ?? 'nicht angegeben'}»
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}
    </>
  );
}
